<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.settings.driverCommentTypes.detailModal.new.buttonText')"
      @click="driverCommentTypeModalOpen = true"
    />
    <export
      url="/driver_comment_types/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.driverCommentTypes')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <driver-comment-type-modal
    v-model:open="driverCommentTypeModalOpen"
    v-model:comment-type="editedCommentType"
    @saved="refetch"
  />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  ref, reactive, computed, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import Tabs from '@/components/structure/navigation/Tabs.vue';
import DriverCommentTypeModal from '@pages/settings/DriverCommentTypeModal.vue';
import { format } from 'date-fns';
import type { HydraCollection, VideoCategory } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
}));

const driverCommentTypeModalOpen = ref(false);
const editedCommentType = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteDriverCommentType, { loading: deleting }] = useDelete();

const totalFilter = computed(() => ({ ...filter.value, ...fixedFilter.value }));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.settings.driverCommentTypes.actions.edit'),
    action: (item: VideoCategory) => {
      driverCommentTypeModalOpen.value = true;
      editedCommentType.value = item;
    },
  },
  // {
  //   title: t('pages.settings.driverCommentTypes.actions.delete'),
  //   action: async (item: VideoCategory) => {
  //     if (
  //       await deleteDriverCommentType(`/driver_comment_types/${item.id}`, {
  //         method: 'PUT',
  //         body: {
  //           deleted: true,
  //         },
  //       })
  //     ) {
  //       // eslint-disable-next-line no-use-before-define
  //       refetch();
  //     }
  //   },
  // },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.settings.driverCommentTypes.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'text',
    title: t('driverCommentType.text'),
    group: t('pages.settings.driverCommentTypes.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.settings.driverCommentTypes.filterGroups.general'),
      options: {
        label: t('driverCommentType.text'),
      },
    },
  },
  {
    key: 'deleted',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driverCommentType.deleted'),
    group: t('pages.settings.driverCommentTypes.fieldGroups.general'),
    visible: true,
    formatter: (v: boolean) => (v ? t('yes') : t('no')),
    filter: {
      type: 'bool',
      group: t('pages.settings.driverCommentTypes.filterGroups.general'),
      options: {
        label: t('driverCommentType.deleted'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.settings.driverCommentTypes.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.settings.driverCommentTypes.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<VideoCategory>>('/driver_comment_types', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

